/**
 * @file chat-texts.ts
 * @description Все строки окна «Сообщения» (chat-window-design.md §5, §7;
 * SC64). Тексты — данными в одном месте: компоненты их не сочиняют, тесты
 * сверяют с проектом.
 */

import { pluralRu } from "../../lib/plural";

export const CHAT_TEXT = {
  windowTitle: "Сообщения",
  expand: "Развернуть",
  shrink: "Уменьшить",
  collapse: "Свернуть",
  back: "К списку",
  info: "Сведения",

  tabsLabel: "Диалоги",
  tabAll: "Все",
  tabMine: "Мои",
  tabArchive: "Архив",
  unassigned: "Без ответственного",
  searchPlaceholder: "Клиент или номер заказа",
  searchLabel: "Поиск диалога",
  customerFilter: (name: string) => `Клиент: ${name}`,
  customerFilterReset: "Снять отбор по клиенту",
  showMore: "Показать ещё",
  newConversations: (n: number) => `Новые диалоги: ${n}`,

  generalChat: "Общий чат",
  order: (number: string) => `Заказ ${number}`,
  noMessages: "нет сообщений",
  you: "Вы",
  leadsYou: "ведёт: вы",
  leads: (name: string) => `ведёт: ${name}`,
  pillTransferred: "Передан вам",
  pillUnarchived: "Из архива",
  pillUnassigned: "Без ответственного",
  yesterday: "вчера",
  unreadAria: (n: number) => `${n} ${pluralRu(n, ["непрочитанное", "непрочитанных", "непрочитанных"])}`,

  // Состояния списка (§5)
  emptyAll: "Диалогов пока нет. Они появятся, когда клиент напишет из личного кабинета на сайте",
  emptyMine: "На вас не назначено ни одного диалога.",
  emptyMineHint: "Возьмите диалог из вкладки «Все»",
  openAll: "Открыть «Все»",
  emptyArchive: "В архиве пусто",
  emptyUnassigned: "Все диалоги распределены",
  resetFilter: "Сбросить отбор",
  notFound: (q: string) => `Ничего не найдено по «${q}». Ищем по имени клиента и номеру заказа`,
  listError: "Не удалось загрузить диалоги. Проверьте связь и попробуйте ещё раз",
  listStale: "Нет связи — список может быть устаревшим. Повторим автоматически",
  retry: "Повторить",
  conversationError: "Не удалось открыть диалог",
  pickConversation: "Выберите диалог, чтобы прочитать переписку и ответить",
  unreadConversations: (n: number) => `Непрочитанных диалогов: ${n}`,
  emptyConversation:
    "Сообщений пока нет. Напишите первым: клиент увидит ответ от имени магазина, а если он не заходил на сайт 30 минут, получит письмо",
  noAccess: "Нет доступа к переписке с клиентами",

  // Лента (§3)
  today: "Сегодня",
  yesterdayTitle: "Вчера",
  newMessagesLine: "Новые сообщения",
  newMessagesPill: (n: number) => `Новые сообщения · ${n}`,
  showFull: "Показать полностью",
  advertising: "Реклама",
  sending: "Отправляется…",
  sent: "Отправлено",
  readByCustomer: "Клиент прочитал",
  failed: "Не отправлено. Текст сохранён",
  delete: "Удалить",
  copyText: "Скопировать текст",
  copied: "Текст скопирован",
  serviceRecords: (n: number) => `Служебные записи · ${n}`,
  serviceCollapsed: (n: number) =>
    `${n} ${pluralRu(n, ["служебная запись", "служебные записи", "служебных записей"])}`,
  serviceNote: "видно только сотрудникам",
  automatic: "Автоматически:",
  /** Причина автоматического «в архив» (R4.17) — по коду сервера `reason`. */
  archiveReason: { order_completed: "заказ завершён", inactive: "давно без сообщений" },
  archiveReasonUnknown: "по сроку",
  transferredBanner: (who: string, when: string) => `Диалог передан вам — ${who}, ${when}`,
  understood: "Понятно",
  feedLabel: "Переписка",

  // Поле ввода и полосы (§4, §7)
  composerPlaceholder: "Сообщение клиенту…",
  send: "Отправить",
  marketing: "Рекламное сообщение",
  marketingHint: "Только для клиентов, согласившихся получать рекламу. Клиент увидит пометку «Реклама»",
  marketingNoConsent: "Клиент не давал согласия на рекламу",
  stripForeign: (name: string) =>
    `Диалог ведёт ${name}. Ваш ответ клиент увидит от имени магазина, ответственный не изменится.`,
  stripUnassigned: "У диалога нет ответственного.",
  stripArchived: "Диалог в архиве. Если клиент напишет, он вернётся сам.",
  stripReadOnly:
    "Вы работаете под учётной записью сотрудника — переписку можно только читать. Ответить клиенту может только сам сотрудник.",
  stripLost: "Диалог больше не назначен вам — он вернулся в общий список",
  toList: "К списку",

  // Действия (§4, §7)
  take: "Взять себе",
  transfer: "Передать…",
  returnToQueue: "Вернуть оператору",
  archive: "В архив",
  unarchive: "Вернуть из архива",
  openOrder: "Открыть заказ",
  openCustomer: "Карточка клиента",
  hideService: "Скрыть служебные записи",
  showService: "Показать служебные записи",
  cancel: "Отмена",
  menuLabel: "Действия с диалогом",
  leadsNow: "Ведёт",
  nobody: "никто",
  orderLeads: (name: string) => `заказ ведёт ${name}`,

  toastTaken: "Диалог взят вами",
  toastTransferred: (name: string) => `Диалог передан: ${name}`,
  toastReturned: "Диалог возвращён оператору",
  toastArchived: "Диалог в архиве",
  toastArchivedUndo: "Вернуть",
  toastUnarchived: "Диалог возвращён из архива",
  toastTransferFailed: "Не удалось передать диалог. Попробуйте ещё раз",
  raceChanged: (who: string | null, archived: boolean) =>
    archived
      ? "Пока вы выбирали, диалог отправили в архив. Данные обновлены"
      : who
        ? `Пока вы выбирали, диалог передали: ${who}. Данные обновлены`
        : "Пока вы выбирали, у диалога сняли ответственного. Данные обновлены",

  confirmReturnTitle: "Вернуть диалог оператору?",
  confirmReturnText: "Он пропадёт из вашего списка, его увидят владелец и оператор заказов.",
  confirmReturnAction: "Вернуть",

  // Передать (§4, §7)
  transferTitle: "Передать диалог",
  transferSearch: "Имя сотрудника",
  transferLeadsOrder: "Ведёт заказ",
  transferOthers: "Остальные",
  transferCurrent: "ведёт сейчас",
  transferSubmit: "Передать",
  transferEmpty:
    "Передать некому: право «Переписка с клиентами» есть только у вас. Выдать право можно в разделе «Сотрудники»",
  conversationsCount: (n: number) => `${n} ${pluralRu(n, ["диалог", "диалога", "диалогов"])}`,

  // Панель сведений (§1в)
  infoCustomer: "Клиент",
  infoOrder: (number: string) => `Заказ ${number}`,
  infoOrderLeads: (name: string) => `Заказ ведёт: ${name}`,
  infoDialog: "Диалог",
  infoHistory: "История диалога",
  infoShowAll: (n: number) => `Показать все (${n})`,
  infoConsentYes: "Согласие на рекламу: есть",
  infoConsentNo: "Согласие на рекламу: нет",
  infoResponseTime: "Время ответа появится позже",

  // Кнопка и подсказка (§1а, §7)
  launcher: (n: number) =>
    n > 0 ? `Сообщения · ${n} ${pluralRu(n, ["непрочитанный диалог", "непрочитанных диалога", "непрочитанных диалогов"])}` : "Сообщения",
  launcherAria: (n: number) => (n > 0 ? `Сообщения, непрочитанных диалогов: ${n}` : "Сообщения"),
  hintMessage: (ctx: string) => `Новое сообщение · ${ctx}`,
  hintTransferred: (who: string | null) => (who ? `Диалог передан вам · ${who}` : "Диалог передан вам"),
  hintUnarchived: (ctx: string) => `Диалог вернулся из архива · ${ctx}`,
  hintMore: (n: number) => `и ещё ${n}`,
  hintClose: "Скрыть подсказку",

  noConversationReadOnly: "Диалога с клиентом ещё нет; создать его под сотрудником нельзя",
  noticeClose: "Скрыть сообщение",
  /** Над лентой, пока сервер говорит «есть ранние» (`hasMore`). */
  showEarlier: "Показать ранние сообщения",
  earlierFailed: "Не удалось загрузить ранние сообщения. Попробуйте ещё раз",
} as const;
